/**
 * A SECURE BACKEND REFUSAL MUST NOT LOOK LIKE A FRONTEND FAILURE.
 *
 * AUTHORITY-CONTAINMENT-0 closed eight doors that were open on shipped code.
 * It closed them by REFUSING — and a refusal that renders as "something went
 * wrong" teaches a user to retry the thing the platform just protected them
 * from. This module is the translation layer, and it has exactly one job:
 * say what the backend said, and never more.
 *
 * Three rules, each with a test:
 *
 *  1. **THE OUTCOME IS READ, NEVER ASSUMED.** This is F-AUTONOMY-0's rule
 *     (features/autonomy/autonomyPresentation.js) applied to a second
 *     contract. `POST /operator/actions/{id}/approve` returns HTTP **200**
 *     with the action body, and that body's `status` can be `"failed"`. A 2xx
 *     is NECESSARY for execution and NOT SUFFICIENT. Verified against
 *     backend/routes/operator.py:465-475 — the route commits, refreshes and
 *     returns `action_to_dict(action)` whatever the outcome was.
 *
 *  2. **A PROTECTIVE REFUSAL IS NOT A FAILURE.** `409 EXECUTION_TRUTH_EXISTS`
 *     means the backend declined to destroy execution records. That is the
 *     platform working. It renders as what was PROTECTED, using the counts the
 *     backend supplied — never counts computed here.
 *
 *  3. **AUTHORITY IS NEVER DERIVED HERE.** Nothing in this module decides who
 *     may do what, whether an action is stale, whether a target is in scope,
 *     or whether execution truth permits a refine. Those are backend answers.
 *     This module has no capability table and no role list, deliberately.
 */

/* -------------------------------------------------------------------------
 * THE OUTCOME OF AN OPERATOR ACTION
 *
 * Read off `status`. The backend's vocabulary, verified in
 * backend/routes/operator.py and backend/services/operator_tools.py:
 *   requested | needs_confirmation | confirmed | executing
 *   executed  | failed | refused | denied
 * ---------------------------------------------------------------------- */

const OUTCOMES = {
  EXECUTED: {
    key: 'EXECUTED',
    label: 'Executed',
    tone: 'executed',
    meaning: 'The server states this action ran and its change is recorded.',
    settled: true,
  },
  AWAITING_CONFIRMATION: {
    key: 'AWAITING_CONFIRMATION',
    label: 'Awaiting human confirmation',
    tone: 'held',
    // Two clauses on purpose. The second is the half a reader skips.
    meaning: 'This action is prepared and is being held. Nothing has been executed.',
    settled: false,
  },
  REFUSED: {
    key: 'REFUSED',
    label: 'Refused',
    tone: 'refused',
    meaning: 'The server refused this action. Nothing was changed.',
    settled: true,
  },
  DENIED: {
    key: 'DENIED',
    label: 'Denied',
    tone: 'refused',
    meaning: 'This action was denied by a person. Nothing was changed.',
    settled: true,
  },
  FAILED: {
    key: 'FAILED',
    label: 'Failed',
    tone: 'failed',
    meaning: 'The server accepted the request and the action did not complete.',
    settled: true,
  },
  PREPARED: {
    key: 'PREPARED',
    label: 'Prepared',
    tone: 'held',
    meaning: 'This action exists as a draft. Nothing has been executed.',
    settled: false,
  },
  EXECUTING: {
    key: 'EXECUTING',
    label: 'Executing',
    tone: 'held',
    meaning: 'The server has claimed this action and is running it.',
    settled: false,
  },
  NOT_STATED: {
    key: 'NOT_STATED',
    label: 'The server did not state an outcome',
    tone: 'unknown',
    meaning:
      'This response carries no recognised action status. Nothing has been assumed in either direction — check the action record before acting on it.',
    settled: false,
  },
};

const STATUS_TO_OUTCOME = {
  executed: 'EXECUTED',
  needs_confirmation: 'AWAITING_CONFIRMATION',
  confirmed: 'AWAITING_CONFIRMATION',
  refused: 'REFUSED',
  denied: 'DENIED',
  failed: 'FAILED',
  requested: 'PREPARED',
  executing: 'EXECUTING',
};

/**
 * `OUTCOMES[key]` alone finds INHERITED properties: a status of "constructor",
 * "toString" or "__proto__" resolved to a Function and skipped the NOT_STATED
 * fallback entirely, returning an outcome object with no key, label or tone.
 * Own-property lookup only.
 */
export const actionOutcomeDisplay = (key) =>
  (Object.prototype.hasOwnProperty.call(OUTCOMES, key) ? OUTCOMES[key] : null) || OUTCOMES.NOT_STATED;

/**
 * Read an action response. NEVER infer execution from the HTTP status.
 *
 * @param {object|null} body the parsed action body from a 2xx response
 * @returns {{outcome: object, actionId: string|null, toolName: string|null,
 *            executed: boolean, error: string|null}}
 */
export const presentActionResponse = (body) => {
  const status = typeof body?.status === 'string' ? body.status : null;
  // Own-property lookup: a status of "constructor" / "toString" / "__proto__"
  // hits Object.prototype and resolves to a Function, which is truthy, so the
  // NOT_STATED fallback was skipped and the caller got an outcome with no key,
  // label or tone. Unlikely from this backend -- but "unrecognised resolves to
  // NOT_STATED" is the rule, and a rule with a hole is not a rule.
  const key =
    (status && Object.prototype.hasOwnProperty.call(STATUS_TO_OUTCOME, status)
      ? STATUS_TO_OUTCOME[status]
      : null) || 'NOT_STATED';
  const outcome = OUTCOMES[key];
  const result = body?.result_json && typeof body.result_json === 'object' ? body.result_json : null;
  return {
    outcome,
    actionId: body?.id ?? null,
    toolName: body?.tool_name ?? null,
    // The single boolean any caller should branch on before claiming success.
    executed: key === 'EXECUTED',
    error: (result && (result.error || result.message)) || null,
  };
};

/* -------------------------------------------------------------------------
 * WHY AN ACTION IS BEING HELD
 *
 * backend/services/operator_tools.py sets result_json.risk to "guarded" when
 * the TOOL demands confirmation, and "model_proposed" when the assistant's
 * router chose it. The second is new in AUTHORITY-CONTAINMENT-0 and is the
 * one the existing UI could not name: it rendered every held action as
 * "guarded", so a model-chosen write looked like a routine one.
 * ---------------------------------------------------------------------- */

const HOLD_REASONS = {
  model_proposed: {
    key: 'model_proposed',
    heading: 'OMNΛS prepared an action',
    lead: 'This action was proposed from the assistant conversation. Nothing has been executed.',
  },
  guarded: {
    key: 'guarded',
    heading: 'This action needs confirmation',
    lead: 'This action is guarded and is being held for a person to confirm. Nothing has been executed.',
  },
};

const HOLD_UNSTATED = {
  key: 'unstated',
  heading: 'This action needs confirmation',
  lead: 'This action is being held. The server did not state why. Nothing has been executed.',
};

export const holdReasonDisplay = (risk) =>
  // Own-property only, for the same reason actionOutcomeDisplay is: a risk of
  // "constructor" or "toString" resolves to an inherited Function, which is
  // truthy, and the HOLD_UNSTATED fallback would be skipped -- returning an
  // object with no heading and no lead, which is exactly the empty-paragraph
  // defect this slice already fixed once.
  (Object.prototype.hasOwnProperty.call(HOLD_REASONS, risk) ? HOLD_REASONS[risk] : null) ||
  HOLD_UNSTATED;

/**
 * The backend preview is not yet a consequence snapshot. AUTHORITY-REGISTRY-1
 * owns publishing one; until it does, a surface that renders only
 * `preview.summary` must not imply the reader is seeing the whole commercial
 * consequence. `award_bid`'s preview, for one, carries no vendor at all.
 *
 * This does NOT disable approval — the current backend contract does not
 * require that, and inventing the requirement here would be deriving authority.
 */
export const CONSEQUENCE_DETAIL_INCOMPLETE = {
  heading: 'Consequence detail incomplete',
  meaning:
    'The server has not yet published the full consequence record for this action. What is shown below may not be everything this action changes.',
};

/* -------------------------------------------------------------------------
 * WHAT THE PLATFORM PROTECTED
 * ---------------------------------------------------------------------- */

/** Human labels for the census kinds backend/services/execution_truth.py emits. */
const HOLD_LABELS = {
  activity_instances: ['activity instance', 'activity instances'],
  material_status: ['material-status record', 'material-status records'],
  manpower_logs: ['manpower record', 'manpower records'],
  dependencies: ['schedule dependency', 'schedule dependencies'],
  execution_observations: ['execution observation', 'execution observations'],
  requirements_linked_to_activities: [
    'requirement linked to an activity',
    'requirements linked to activities',
  ],
  provider_scope_assignments_linked_to_activities: [
    'provider scope assignment',
    'provider scope assignments',
  ],
};

/**
 * Render every hold the backend supplied, in its order, computing nothing.
 *
 * An unrecognised kind is rendered under its raw key rather than dropped: the
 * backend's census grows with the schema, and silently hiding a protected
 * record class would under-report what was protected.
 */
export const presentProtectedRecords = (holds) => {
  if (!holds || typeof holds !== 'object') return [];
  return Object.entries(holds)
    .filter(([, count]) => typeof count === 'number' && count > 0)
    .map(([kind, count]) => {
      const labels = Object.prototype.hasOwnProperty.call(HOLD_LABELS, kind) ? HOLD_LABELS[kind] : null;
      const noun = labels ? labels[count === 1 ? 0 : 1] : kind;
      return { kind, count, label: `${count} ${noun}` };
    });
};

/* -------------------------------------------------------------------------
 * THE DESTRUCTIVE-SCOPE WARNING
 *
 * "Are you sure?" does not tell a person what category of project state is
 * about to change. This does. It states the CATEGORY of risk and explicitly
 * does not precompute cascade counts — those only exist after the backend
 * refuses and supplies `holds`.
 * ---------------------------------------------------------------------- */

export const REFINE_CONSEQUENCE = {
  heading: 'Refine project plan',
  body: [
    'OMNΛS will replace the current generated activity structure with a newly refined plan.',
    'If execution records already depend on this structure, the server will refuse the change rather than destroy those records.',
  ],
  reassurance: 'No project change has occurred yet.',
  cancelLabel: 'Cancel',
  confirmLabel: 'Review and confirm',
};

/* -------------------------------------------------------------------------
 * REFUSALS
 *
 * Input is a CLASSIFIED error from lib/workflowErrors.js — this module does
 * not parse axios errors itself, so there is one parser and one vocabulary.
 * ---------------------------------------------------------------------- */

const REFUSALS = {
  withheld: {
    kind: 'withheld',
    heading: 'Plan refinement withheld',
    lead:
      'OMNΛS did not replace the project plan because execution records already depend on the existing structure.',
    closing: 'Nothing was deleted.',
    retryAllowed: false,
  },
  confirmation: {
    kind: 'confirmation',
    heading: 'Confirmation required',
    // Deliberately GENERIC. This lead is reached by any 428, and the
    // activity-structure wording belongs to REFINE_CONSEQUENCE, which the
    // refine surface renders on top of it. Stating a consequence the caller
    // may not have is the same class of error as inferring one.
    lead: 'The server is holding this action for a deliberate confirmation. Nothing has been executed.',
    retryAllowed: false,
  },
  'no-access': {
    kind: 'no-access',
    heading: 'You do not have authority for this action',
    lead: 'The server refused this action for this account. Nothing was changed.',
    retryAllowed: false,
  },
  'operator-unavailable': {
    kind: 'operator-unavailable',
    heading: 'Operator not available for this account',
    lead: 'This account has no Operator tools, so Operator surfaces are not available to it. This is not an outage.',
    retryAllowed: false,
  },
  settled: {
    kind: 'settled',
    heading: 'Action already settled',
    lead: 'This prepared action is no longer waiting to run. Reload it to see its current state.',
    retryAllowed: false,
  },
  'not-pending': {
    kind: 'not-pending',
    heading: 'This action is not waiting for confirmation',
    lead: 'Its state has moved since this screen loaded. Reload it to see where it is now.',
    retryAllowed: false,
  },
  'input-refused': {
    kind: 'input-refused',
    heading: 'Action input refused',
    lead: 'The server did not accept this request. Nothing was changed.',
    retryAllowed: false,
  },
};

/**
 * `reference_out_of_scope` deliberately answers identically for "this record
 * does not exist" and "this record belongs to another tenant"
 * (backend/services/operator_authority.py). Rendering it as "not found" would
 * undo that and turn the refusal into an existence oracle.
 */
const INPUT_REFUSAL_CODES = new Set([
  'undeclared_input_key',
  'forbidden_input_key',
  'missing_required_input',
  'reference_out_of_scope',
  // AUTHORITY-CONTAINMENT-1 reuses `reference_out_of_scope` for a node or
  // activity that belongs to another project or tenant, and adds these two for
  // a reference that cannot be proved AT ALL because the request carried no
  // project to scope it to. Both arrive as HTTP 400 {code, message} through
  // backend/routes/operator.py:402, exactly like their siblings above -- the
  // containment slice introduced NO new status codes, which is why this is a
  // two-line reconciliation and not a new presentation state.
  'unscoped_node_reference',
  'unscoped_activity_reference',
]);

/**
 * @param {object} classified the result of classifyWorkflowError()
 * @returns {{kind,heading,lead,closing?,message,retryAllowed,protectedRecords,code,status}}
 */
export const presentRefusal = (classified) => {
  const { kind, code, status, message, detail } = classified || {};
  let base = REFUSALS[kind];

  if (status === 403 && code === 'operator_not_available') base = REFUSALS['operator-unavailable'];
  else if (status === 409 && code === 'EXECUTION_TRUTH_EXISTS') base = REFUSALS.withheld;
  else if (status === 409 && code === 'action_already_settled') base = REFUSALS.settled;
  else if (status === 409 && code === 'action_not_pending_confirmation') base = REFUSALS['not-pending'];
  else if (INPUT_REFUSAL_CODES.has(code)) base = REFUSALS['input-refused'];

  if (!base) {
    return {
      kind: kind || 'server',
      heading: 'Request not completed',
      lead: message || 'The server did not complete this request.',
      message: message || null,
      retryAllowed: kind === 'network' || kind === 'server',
      protectedRecords: [],
      code: code || null,
      status: status ?? null,
    };
  }

  return {
    ...base,
    // The server's own sentence is kept beside ours. Ours says what KIND of
    // answer this is; the server's says what it was about.
    message: message || null,
    protectedRecords: presentProtectedRecords(detail?.holds),
    code: code || null,
    status: status ?? null,
  };
};

/* -------------------------------------------------------------------------
 * WHAT A DESTRUCTIVE ACTION DOES NEXT
 *
 * Extracted from the component so the load-bearing guarantees are unit facts
 * rather than things only a browser can observe. Every branch states
 * `autoRetry: false` explicitly, and a test asserts it on ALL of them: the
 * moment one branch quietly omits it, a 428 becomes a speed bump and a 409
 * becomes a retry loop against a protection.
 * ---------------------------------------------------------------------- */

/** The phase a destructive action should move to after the server answered. */
export const REFINE_PHASES = {
  HOLD_FOR_HUMAN: 'confirming',
  REFUSED: 'idle',
  DONE: 'done',
};

/**
 * Decide what happens after a refusal. Pure.
 *
 * @param {object} classified result of classifyWorkflowError()
 * @returns {{phase: string, refusal: object|null, autoRetry: false,
 *            mayResend: boolean, needsHuman: boolean}}
 */
export const decideAfterRefineRefusal = (classified) => {
  const status = classified?.status ?? null;
  const code = classified?.code ?? null;

  // 428 — the gate that makes confirmation deliberate. It is NOT an error and
  // it is NOT a retry: the client must stop and let a human choose again.
  // Re-sending with confirm=true automatically would defeat the only thing
  // this status exists to do.
  if (status === 428) {
    return {
      phase: REFINE_PHASES.HOLD_FOR_HUMAN,
      refusal: null,
      autoRetry: false,
      mayResend: true, // by a human, on a second explicit act
      needsHuman: true,
    };
  }

  // 409 EXECUTION_TRUTH_EXISTS — the platform declined to destroy records.
  // Resending cannot succeed, because the records are still there. Offering a
  // retry would teach the user to hunt for a way around a protection.
  if (status === 409 && code === 'EXECUTION_TRUTH_EXISTS') {
    return {
      phase: REFINE_PHASES.REFUSED,
      refusal: presentRefusal(classified),
      autoRetry: false,
      mayResend: false,
      needsHuman: false,
    };
  }

  // Everything else — 403 authority, 400 input, 404, network. Rendered as
  // itself, never retried on the client's own initiative.
  return {
    phase: REFINE_PHASES.REFUSED,
    refusal: presentRefusal(classified),
    autoRetry: false,
    mayResend: false,
    needsHuman: false,
  };
};

/**
 * Missing commercial fields mean "not in your access context", never zero.
 * backend/services/operator_command.py filters `estimated_value`, `vendor_id`
 * and vendor contact details for actors without `rfq.read`. A UI that renders
 * an absent amount as $0 states a commercial fact the server refused to state.
 */
export const NOT_IN_ACCESS_CONTEXT = 'Not available in your current access context';

export const presentCommercialField = (value) =>
  value === undefined || value === null
    ? { available: false, text: NOT_IN_ACCESS_CONTEXT }
    : { available: true, text: String(value) };

/**
 * A SETTLED BAD ANSWER INTERRUPTS; EVERYTHING ELSE WAITS ITS TURN.
 *
 * NOT_STATED is in here deliberately. "The backend did not state an outcome"
 * is not a neutral pause -- it is the one case where a user must NOT assume
 * the write happened, so it is the last thing that should queue behind other
 * chatter.
 */
export const INTERRUPTING_OUTCOMES = Object.freeze(['FAILED', 'REFUSED', 'DENIED', 'NOT_STATED']);

/**
 * The sentence a screen reader is given for one action, and how urgently.
 *
 * Pure, so it is tested directly rather than inferred from markup. It reads
 * the SAME presenter output the visible panel reads -- if the two ever drift,
 * a sighted user and a screen-reader user are told different things about a
 * consequential write.
 *
 * A refusal wins over an outcome: on a transport failure both are on screen,
 * and the refusal is the new information (the outcome is the unchanged
 * held state the user already knows about).
 *
 * @param {{outcomeKey?: string|null, toolName?: string|null, refusal?: object|null}} input
 * @returns {{message: string, urgent: boolean}}
 */
export const announceAction = ({ outcomeKey = null, toolName = null, refusal = null } = {}) => {
  if (refusal) {
    return {
      message: [refusal.heading, refusal.lead].filter(Boolean).join('. '),
      urgent: true,
    };
  }
  if (!outcomeKey) return { message: '', urgent: false };
  const display = actionOutcomeDisplay(outcomeKey);
  return {
    message: [toolName ? `${toolName}: ` : '', display.label, '. ', display.meaning].join(''),
    urgent: INTERRUPTING_OUTCOMES.includes(display.key),
  };
};

export default {
  actionOutcomeDisplay,
  announceAction,
  presentActionResponse,
  holdReasonDisplay,
  presentProtectedRecords,
  presentRefusal,
  presentCommercialField,
  REFINE_CONSEQUENCE,
  CONSEQUENCE_DETAIL_INCOMPLETE,
  NOT_IN_ACCESS_CONTEXT,
};
